import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    Input,
    Modal,
    ModalClose,
    ModalDialog,
    Sheet,
    Table,
    Typography,
    Chip
} from '@mui/joy';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { useNavigate } from 'react-router-dom';
import axios from '../util/axiosConfig';
import UserContext from '../context/UserContext';
import { formatDateTime } from '../util/dateFormat';
import QuizQrScanner from './QuizQrScanner';
import QuizLeaderboard from './QuizLeaderboard';
import { parseQuizSessionFromQr } from '../util/parseQuizSessionFromQr';
import { useMobileMenu } from '../context/MobileMenuContext';

const tableSheetSx = {
    width: '100%',
    maxWidth: '100%',
    borderRadius: 'sm',
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: '480px',
    WebkitOverflowScrolling: 'touch',
    border: '1px solid',
    borderColor: 'divider'
};

const quizTableSx = {
    width: 'max-content',
    minWidth: '100%',
    tableLayout: 'fixed',
    '& tbody tr:nth-of-type(even)': { backgroundColor: '#f9f9f9' },
    '& tbody tr': { cursor: 'pointer' },
    '& tbody tr:hover': { backgroundColor: '#fff5ed' },
    '& thead': { position: 'sticky', top: 0, zIndex: 10 },
    '& thead th': {
        backgroundColor: '#eb7a24',
        color: 'white',
        fontWeight: 'bold',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        borderBottom: '2px solid #e55125',
        whiteSpace: 'nowrap'
    },
    '& td, & th': {
        padding: '12px',
        border: '1px solid',
        borderColor: 'divider',
        verticalAlign: 'middle',
        overflow: 'hidden'
    },
    '& th:nth-of-type(1), & td:nth-of-type(1)': { width: 200, minWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word' },
    '& th:nth-of-type(2), & td:nth-of-type(2)': { width: 100, minWidth: 90, whiteSpace: 'nowrap' },
    '& th:nth-of-type(3), & td:nth-of-type(3)': { width: 180, minWidth: 160, whiteSpace: 'nowrap' },
    '& th:nth-of-type(4), & td:nth-of-type(4)': { width: 90, minWidth: 80, whiteSpace: 'nowrap' },
    '& th:nth-of-type(5), & td:nth-of-type(5)': { width: 90, minWidth: 80, whiteSpace: 'nowrap' }
};

const activeTableSx = {
    ...quizTableSx,
    '& th:nth-of-type(3), & td:nth-of-type(3)': { width: 110, minWidth: 100, whiteSpace: 'nowrap' },
    '& th:nth-of-type(4), & td:nth-of-type(4)': { width: 100, minWidth: 90, whiteSpace: 'nowrap' },
    '& th:nth-of-type(5), & td:nth-of-type(5)': { width: 100, minWidth: 90, whiteSpace: 'nowrap' }
};

const formatSessionStatus = (status) => {
    if (status === 'active') return 'In progress';
    if (status === 'waiting') return 'Lobby';
    return status || '-';
};

const ActiveQuizListItem = ({ quiz, onJoin }) => (
    <Box
        onClick={() => onJoin(quiz.sessionId)}
        onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onJoin(quiz.sessionId);
            }
        }}
        tabIndex={0}
        role="button"
        aria-label={`Join quiz ${quiz.quizTitle}`}
        sx={{
            p: 1.5,
            borderRadius: 'sm',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.surface',
            cursor: 'pointer',
            '&:hover': { bgcolor: '#fff5ed' },
            '&:nth-of-type(even)': { bgcolor: '#f9f9f9', '&:hover': { bgcolor: '#fff5ed' } }
        }}
    >
        <Typography level="title-sm" sx={{ wordBreak: 'break-word' }}>
            {quiz.quizTitle}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                {quiz.sessionId}
            </Typography>
            <Chip
                size="sm"
                variant="soft"
                color={quiz.status === 'active' ? 'success' : 'warning'}
            >
                {formatSessionStatus(quiz.status)}
            </Chip>
            {quiz.joined && (
                <Chip size="sm" variant="soft" color="primary">
                    Joined
                </Chip>
            )}
        </Box>
        <Button
            size="sm"
            fullWidth
            onClick={(e) => {
                e.stopPropagation();
                onJoin(quiz.sessionId);
            }}
            sx={{
                mt: 1,
                bgcolor: '#ea7a23',
                '&:hover': { bgcolor: '#d06a1e' }
            }}
        >
            {quiz.joined ? 'Open quiz' : 'Join quiz'}
        </Button>
    </Box>
);

const CompletedQuizListItem = ({ quiz, onSelect }) => (
    <Box
        onClick={() => onSelect(quiz)}
        onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(quiz);
            }
        }}
        tabIndex={0}
        role="button"
        aria-label={`View leaderboard for ${quiz.quizTitle}`}
        sx={{
            p: 1.5,
            borderRadius: 'sm',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.surface',
            cursor: 'pointer',
            '&:hover': { bgcolor: '#fff5ed' },
            '&:nth-of-type(even)': { bgcolor: '#f9f9f9', '&:hover': { bgcolor: '#fff5ed' } }
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
            <Typography level="title-sm" sx={{ wordBreak: 'break-word', flex: 1 }}>
                {quiz.quizTitle}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end', flexShrink: 0 }}>
                {quiz.myRank > 0 && (
                    <Chip size="sm" variant="soft" color="primary">
                        Rank {quiz.myRank}
                        {quiz.participantCount > 0 ? ` of ${quiz.participantCount}` : ''}
                    </Chip>
                )}
                <Chip size="sm" variant="soft" color="warning">
                    Score {Number(quiz.myScore || 0)}
                </Chip>
            </Box>
        </Box>
        <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.75 }}>
            {quiz.sessionId}
        </Typography>
        <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.25 }}>
            {formatDateTime(quiz.completedAt)}
        </Typography>
    </Box>
);

const QuizHome = () => {
    const navigate = useNavigate();
    const { user } = useContext(UserContext);
    const { isMobile } = useMobileMenu();
    const [sessionId, setSessionId] = useState('');
    const [scannerOpen, setScannerOpen] = useState(false);
    const [scanError, setScanError] = useState('');

    const [activeLoading, setActiveLoading] = useState(true);
    const [activeQuizzes, setActiveQuizzes] = useState([]);

    const [completedLoading, setCompletedLoading] = useState(true);
    const [completedError, setCompletedError] = useState('');
    const [completedQuizzes, setCompletedQuizzes] = useState([]);
    const [leaderboardOpen, setLeaderboardOpen] = useState(false);
    const [selectedQuiz, setSelectedQuiz] = useState(null);

    const currentStudentId = user?.studentId || user?.id;

    const joinSession = (code) => {
        const trimmed = (code || '').trim();
        if (!trimmed) return;
        navigate(`/quiz/${encodeURIComponent(trimmed)}`);
    };

    const goToQuiz = () => joinSession(sessionId);

    const handleQrScan = useCallback((rawText) => {
        const code = parseQuizSessionFromQr(rawText);
        if (!code) {
            setScanError('Could not read a valid quiz code from that QR.');
            return;
        }
        setScanError('');
        setSessionId(code);
        setScannerOpen(false);
        joinSession(code);
    }, [navigate]);

    const loadActiveQuizzes = useCallback(async () => {
        if (!currentStudentId) {
            setActiveQuizzes([]);
            setActiveLoading(false);
            return;
        }

        try {
            const res = await axios.get('/quizzes/active');
            if (!res?.data?.success) {
                throw new Error(res?.data?.errorMessage || 'Failed to load active quizzes');
            }
            setActiveQuizzes(Array.isArray(res.data.data) ? res.data.data : []);
        } catch (e) {
            console.error('Active quizzes load error:', e);
            setActiveQuizzes([]);
        } finally {
            setActiveLoading(false);
        }
    }, [currentStudentId]);

    const loadCompletedQuizzes = useCallback(async () => {
        if (!currentStudentId) {
            setCompletedQuizzes([]);
            setCompletedLoading(false);
            return;
        }

        try {
            setCompletedError('');
            const res = await axios.get('/quizzes/completed');
            if (!res?.data?.success) {
                throw new Error(res?.data?.errorMessage || 'Failed to load completed quizzes');
            }
            setCompletedQuizzes(Array.isArray(res.data.data) ? res.data.data : []);
        } catch (e) {
            setCompletedError(e?.response?.data?.errorMessage || e?.message || 'Failed to load completed quizzes');
            setCompletedQuizzes([]);
        } finally {
            setCompletedLoading(false);
        }
    }, [currentStudentId]);

    useEffect(() => {
        loadActiveQuizzes();
        loadCompletedQuizzes();
    }, [loadActiveQuizzes, loadCompletedQuizzes]);

    const openLeaderboard = (quiz) => {
        setSelectedQuiz(quiz);
        setLeaderboardOpen(true);
    };

    const closeLeaderboard = () => {
        setLeaderboardOpen(false);
        setSelectedQuiz(null);
    };

    const hasActiveQuizzes = !activeLoading && activeQuizzes.length > 0;

    return (
        <Box sx={{ width: '100%', maxWidth: 720, mx: 'auto', mt: 1, minWidth: 0 }}>
            <Card variant="outlined" sx={{ borderRadius: 'md' }}>
                <CardContent sx={{ minWidth: 0 }}>
                    <Typography level="h3" sx={{ mb: 1 }}>
                        Live Quiz
                    </Typography>

                    {hasActiveQuizzes && (
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <PlayCircleOutlineIcon sx={{ color: '#ea7a23' }} />
                                <Typography level="title-md">Active for your batch</Typography>
                            </Box>
                            <Typography level="body-sm" sx={{ mb: 1.5, color: 'text.secondary' }}>
                                {isMobile
                                    ? 'Tap a quiz below to join or re-enter the session.'
                                    : 'Tap a row to join or re-enter the session.'}
                            </Typography>
                            {isMobile ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {activeQuizzes.map((quiz) => (
                                        <ActiveQuizListItem
                                            key={quiz.sessionId}
                                            quiz={quiz}
                                            onJoin={joinSession}
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <Sheet variant="outlined" sx={{ ...tableSheetSx, maxHeight: '320px' }}>
                                    <Table stickyHeader sx={activeTableSx}>
                                        <thead>
                                            <tr>
                                                <th>Quiz</th>
                                                <th>Code</th>
                                                <th>Status</th>
                                                <th>Joined</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {activeQuizzes.map((quiz) => (
                                                <tr
                                                    key={quiz.sessionId}
                                                    onClick={() => joinSession(quiz.sessionId)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            joinSession(quiz.sessionId);
                                                        }
                                                    }}
                                                    tabIndex={0}
                                                    role="button"
                                                    aria-label={`Join quiz ${quiz.quizTitle}`}
                                                >
                                                    <td>{quiz.quizTitle}</td>
                                                    <td>{quiz.sessionId}</td>
                                                    <td>
                                                        <Chip
                                                            size="sm"
                                                            variant="soft"
                                                            color={quiz.status === 'active' ? 'success' : 'warning'}
                                                        >
                                                            {formatSessionStatus(quiz.status)}
                                                        </Chip>
                                                    </td>
                                                    <td>
                                                        <Chip
                                                            size="sm"
                                                            variant="soft"
                                                            color={quiz.joined ? 'primary' : 'neutral'}
                                                        >
                                                            {quiz.joined ? 'Yes' : 'No'}
                                                        </Chip>
                                                    </td>
                                                    <td>
                                                        <Button
                                                            size="sm"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                joinSession(quiz.sessionId);
                                                            }}
                                                            sx={{
                                                                bgcolor: '#ea7a23',
                                                                '&:hover': { bgcolor: '#d06a1e' }
                                                            }}
                                                        >
                                                            {quiz.joined ? 'Open' : 'Join'}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Sheet>
                            )}
                            <Divider sx={{ my: 2 }}>or enter a code</Divider>
                        </Box>
                    )}

                    {!hasActiveQuizzes && (
                        <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
                            Enter the quiz code shown by your faculty (example: SW4669), or scan the QR code.
                        </Typography>
                    )}

                    {scanError && (
                        <Alert color="danger" variant="soft" sx={{ mb: 1.5 }} onClose={() => setScanError('')}>
                            {scanError}
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                        <Input
                            value={sessionId}
                            onChange={(e) => setSessionId(e.target.value)}
                            placeholder="Quiz code (sessionId)"
                            sx={{
                                flex: 1,
                                minWidth: 220,
                                '&:hover': { borderColor: '#ea7a23' },
                                '&:focus-within': { borderColor: '#ea7a23' }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') goToQuiz();
                            }}
                        />
                        <Button
                            onClick={goToQuiz}
                            sx={{
                                bgcolor: '#ea7a23',
                                '&:hover': { bgcolor: '#d06a1e' }
                            }}
                        >
                            Join
                        </Button>
                    </Box>

                    <Divider sx={{ my: 1.5 }}>or</Divider>

                    <Button
                        variant="outlined"
                        color="warning"
                        startDecorator={<QrCodeScannerIcon />}
                        onClick={() => {
                            setScanError('');
                            setScannerOpen(true);
                        }}
                        sx={{
                            width: '100%',
                            borderColor: '#ea7a23',
                            color: '#ea7a23',
                            '&:hover': { borderColor: '#d06a1e', bgcolor: '#fff5ed' }
                        }}
                    >
                        Scan QR code
                    </Button>
                </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 'md', mt: 2 }}>
                <CardContent sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <EmojiEventsIcon sx={{ color: '#ea7a23' }} />
                        <Typography level="h4">Completed quizzes</Typography>
                    </Box>
                    <Typography level="body-sm" sx={{ mb: 1.5, color: 'text.secondary' }}>
                        {isMobile
                            ? 'Tap a quiz below to view the final leaderboard.'
                            : 'Tap a row to view the final leaderboard for that session.'}
                    </Typography>

                    {completedError && (
                        <Alert color="danger" variant="soft" sx={{ mb: 1.5 }}>
                            {completedError}
                        </Alert>
                    )}

                    {completedLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress size="sm" />
                        </Box>
                    ) : completedQuizzes.length === 0 ? (
                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                            No completed quizzes yet. Join a live quiz to see results here after it ends.
                        </Typography>
                    ) : isMobile ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {completedQuizzes.map((quiz) => (
                                <CompletedQuizListItem
                                    key={quiz.sessionId}
                                    quiz={quiz}
                                    onSelect={openLeaderboard}
                                />
                            ))}
                        </Box>
                    ) : (
                        <Sheet variant="outlined" sx={tableSheetSx}>
                            <Table stickyHeader sx={quizTableSx}>
                                <thead>
                                    <tr>
                                        <th>Quiz</th>
                                        <th>Code</th>
                                        <th>Completed</th>
                                        <th>Rank</th>
                                        <th>Your score</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {completedQuizzes.map((quiz) => (
                                        <tr
                                            key={quiz.sessionId}
                                            onClick={() => openLeaderboard(quiz)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    openLeaderboard(quiz);
                                                }
                                            }}
                                            tabIndex={0}
                                            role="button"
                                            aria-label={`View leaderboard for ${quiz.quizTitle}`}
                                        >
                                            <td>{quiz.quizTitle}</td>
                                            <td>{quiz.sessionId}</td>
                                            <td>{formatDateTime(quiz.completedAt)}</td>
                                            <td>
                                                {quiz.myRank > 0
                                                    ? `${quiz.myRank}${quiz.participantCount ? ` / ${quiz.participantCount}` : ''}`
                                                    : '-'}
                                            </td>
                                            <td>{Number(quiz.myScore || 0)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Sheet>
                    )}
                </CardContent>
            </Card>

            <QuizQrScanner
                open={scannerOpen}
                onClose={() => setScannerOpen(false)}
                onScan={handleQrScan}
            />

            <Modal open={leaderboardOpen} onClose={closeLeaderboard}>
                <ModalDialog
                    aria-labelledby="quiz-leaderboard-title"
                    sx={{
                        width: '100%',
                        maxWidth: 640,
                        maxHeight: '90vh',
                        overflow: 'auto'
                    }}
                >
                    <ModalClose />
                    <Typography id="quiz-leaderboard-title" level="h4" sx={{ pr: 4 }}>
                        {selectedQuiz?.quizTitle || 'Quiz leaderboard'}
                    </Typography>
                    {selectedQuiz?.sessionId && (
                        <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 1 }}>
                            Session {selectedQuiz.sessionId}
                        </Typography>
                    )}
                    {selectedQuiz?.sessionId && (
                        <QuizLeaderboard
                            sessionId={selectedQuiz.sessionId}
                            currentStudentId={currentStudentId}
                            variant="final"
                        />
                    )}
                </ModalDialog>
            </Modal>
        </Box>
    );
};

export default QuizHome;
